import { useRouter } from "next/router";
import { useEffect, useRef } from "react";
import {
  useAppDispatch,
  useAppSelector,
} from "../../../services/hooks/useReduxHooks";
import { selectGuestTiersEnabled } from "../../../services/redux/selectors/loyaltyServiceSelectors";
import {
  decreasePendingOperations,
  increasePendingOperations,
  setLoading,
} from "../../../services/redux/slices/globalComponentsToggleSlice";
import { PagesEnum } from "../../../utils/enums";
import { getPageLink } from "../../../utils/help/navigation/navigation";

/** Routes used by the tier flow, prefetched while members browse the loyalty tab. */
const TIER_ROUTES = [
  PagesEnum.LoyaltyTiers,
  PagesEnum.AllRewards,
  PagesEnum.OurPolicies,
] as const;

const isTierRoute = (url: string) =>
  TIER_ROUTES.some((route) => url.includes(route));

/**
 * How long a tier-route transition may run before it is worth covering with the
 * app-wide loader. These routes are prefetched above and their screens render
 * from the store rather than fetching, so a warm transition finishes well inside
 * this window - raising the loader on `routeChangeStart` only flashed a
 * full-screen spinner over a navigation that had effectively already happened
 * (prepits/project#4107). A cold chunk or a slow connection still overruns it
 * and gets the loader as before.
 */
const ROUTE_LOADER_DELAY_MS = 200;

/**
 * Prefetches tier-flow routes and feeds their transitions into the app-wide
 * loading system, including its pending-operation guard and watchdog.
 */
export const LoyaltyRouteLoader = () => {
  const router = useRouter();
  const dispatch = useAppDispatch();
  const tiersEnabled = useAppSelector(selectGuestTiersEnabled);
  const loadingStateSolution = useAppSelector(
    (state) => state.config.restaurantInfo?.config?.loadingStateSolution
  );
  const routeLoadingRef = useRef(false);
  const routeLoaderTimerRef = useRef<ReturnType<typeof setTimeout>>();

  useEffect(() => {
    if (!tiersEnabled) {
      return;
    }

    [...TIER_ROUTES, PagesEnum.Home].forEach((page) => {
      const link = getPageLink(router, page);

      router.prefetch(link.href, link.as).catch(() => undefined);
    });
  }, [router, tiersEnabled]);

  useEffect(() => {
    if (!tiersEnabled) {
      return;
    }

    const stop = () => {
      // Cancel a loader that was armed but never shown - the transition beat it.
      if (routeLoaderTimerRef.current) {
        clearTimeout(routeLoaderTimerRef.current);
        routeLoaderTimerRef.current = undefined;
      }

      if (!routeLoadingRef.current) {
        return;
      }

      routeLoadingRef.current = false;
      loadingStateSolution
        ? dispatch(decreasePendingOperations())
        : dispatch(setLoading(false));
    };
    const start = (url: string) => {
      if (
        routeLoadingRef.current ||
        routeLoaderTimerRef.current ||
        (!isTierRoute(url) && !isTierRoute(router.asPath))
      ) {
        return;
      }

      // Armed, not raised. `stop` clears this, so only a transition that
      // outlasts the delay ever reaches the loading store - which keeps the
      // increase/decrease pairing (and its watchdog) balanced either way.
      routeLoaderTimerRef.current = setTimeout(() => {
        routeLoaderTimerRef.current = undefined;
        routeLoadingRef.current = true;
        loadingStateSolution
          ? dispatch(increasePendingOperations())
          : dispatch(setLoading(true));
      }, ROUTE_LOADER_DELAY_MS);
    };

    router.events.on("routeChangeStart", start);
    router.events.on("routeChangeComplete", stop);
    router.events.on("routeChangeError", stop);

    return () => {
      router.events.off("routeChangeStart", start);
      router.events.off("routeChangeComplete", stop);
      router.events.off("routeChangeError", stop);
      stop();
    };
  }, [
    dispatch,
    loadingStateSolution,
    router.asPath,
    router.events,
    tiersEnabled,
  ]);

  return null;
};
